import { BulkJob } from "admin/hooks/useBulkJobs";
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { cn } from "~/lib/utils";

interface BulkJobBannerProps {
  job: BulkJob | null;
  onDismiss: () => void;
  currentIndex: number;
  total: number;
  goNext: () => void;
  goPrev: () => void;
}

function getIcon(job: BulkJob) {
  if (job.status === "pending" || job.status === "in_progress") {
    return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground shrink-0" />;
  }
  if (job.status === "completed" && job.errors.length === 0) {
    return <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />;
  }
  return <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />;
}

function getLabel(job: BulkJob): string {
  const verb = job.jobType === "create" ? "imported" : "updated";
  const action = job.jobType === "create" ? "import" : "update";
  switch (job.status) {
    case "pending":
      return `Preparing to ${action} ${job.totalUsers} users…`;
    case "in_progress":
      return `${job.processedUsers} / ${job.totalUsers} users ${verb}`;
    case "completed":
      if (job.errors.length === 0) {
        return `Successfully ${verb} ${job.totalUsers} users`;
      }
      return `${job.totalUsers - job.errors.length} / ${job.totalUsers} users ${verb} · ${job.errors.length} failed`;
    case "failed":
      return `Failed to ${action} users`;
  }
}

export function BulkJobBanner({
  job,
  onDismiss,
  currentIndex,
  total,
  goNext,
  goPrev,
}: BulkJobBannerProps) {
  if (!job) {
    return null;
  }

  const visibleErrors = job.errors.slice(0, 3);
  const extraErrors = job.errors.length - visibleErrors.length;

  return (
    <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999] w-fit max-w-2xl bg-background border border-border rounded-lg shadow-lg px-4 py-3 flex items-center gap-3">
      {getIcon(job)}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{getLabel(job)}</p>
        {visibleErrors.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {visibleErrors.map((e, i) => (
              <p key={i} className="text-xs text-red-600">
                {e.email
                  ? `${e.email}: ${e.error}`
                  : e.tag
                    ? `Group "${e.tag}": ${e.error}`
                    : e.error}
              </p>
            ))}
            {extraErrors > 0 && <p className="text-xs text-red-600">+{extraErrors} more</p>}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Started at{" "}
          {job.startedAt.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          })}
        </p>
      </div>

      {total > 1 && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className={cn(
              "p-1 rounded hover:bg-muted",
              currentIndex === 0 && "opacity-30 pointer-events-none"
            )}
            aria-label="Previous job"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-muted-foreground tabular-nums">
            {currentIndex + 1} / {total}
          </span>
          <button
            onClick={goNext}
            disabled={currentIndex === total - 1}
            className={cn(
              "p-1 rounded hover:bg-muted",
              currentIndex === total - 1 && "opacity-30 pointer-events-none"
            )}
            aria-label="Next job"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <button
        onClick={onDismiss}
        className="p-1 rounded hover:bg-muted shrink-0"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
